import React from "react";

export type ButtonVariant =
  "primary" | "secondary" | "outline" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-4 py-2.5 text-sm",
  lg: "px-6 py-3 text-base",
};

/**
 * Reusable button component.
 * "primary" and "secondary" variants derive their colors from CSS custom
 * properties (--brand-primary, --brand-accent) so they update automatically
 * when the company theme changes.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      isLoading = false,
      fullWidth = false,
      disabled,
      children,
      className = "",
      style,
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || isLoading;

    const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
      primary: {
        backgroundColor: "var(--brand-primary)",
        color: "var(--brand-on-chrome)",
        border: "none",
      },
      secondary: {
        backgroundColor: "var(--brand-accent)",
        color: "var(--brand-on-chrome)",
        border: "none",
      },
      outline: {
        backgroundColor: "transparent",
        color: "var(--brand-primary)",
        border: "1.5px solid var(--brand-primary)",
      },
      ghost: {
        backgroundColor: "transparent",
        color: "var(--brand-primary)",
        border: "none",
      },
      danger: {
        backgroundColor: "var(--danger)",
        color: "var(--brand-on-chrome)",
        border: "none",
      },
    };

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        style={{
          ...variantStyles[variant],
          opacity: isDisabled ? 0.6 : 1,
          cursor: isDisabled ? "not-allowed" : "pointer",
          width: fullWidth ? "100%" : undefined,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          fontWeight: 600,
          borderRadius: "10px",
          transition: "opacity 0.15s, transform 0.1s",
          ...style,
        }}
        className={`${SIZE_CLASSES[size]} ${className}`}
        {...rest}
      >
        {isLoading && (
          <svg
            style={{
              animation: "spin 1s linear infinite",
              width: "14px",
              height: "14px",
            }}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeOpacity="0.25" />
            <path d="M3 12a9 9 0 019-9" />
          </svg>
        )}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
export default Button;
